/** Minimum length for new passwords (sign-up, invite accept, change password). */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Strip accidental leading/trailing whitespace from a password field.
 *
 * Paste and autofill often leave a stray space that makes an otherwise-correct
 * password fail verification. Applied on submit (and in the API client) for
 * both new passwords and login — never while the customer is still typing.
 */
export function normalizePasswordInput(password: string): string {
  return password.trim();
}

export type PasswordRuleId = 'minLength' | 'special' | 'uppercase' | 'lowercase' | 'number';

/** Rules shown under password fields, in display order. */
export const PASSWORD_DISPLAY_RULE_IDS = [
  'minLength',
  'special',
  'uppercase',
  'number',
] as const satisfies readonly PasswordRuleId[];

export type PasswordDisplayRuleId = (typeof PASSWORD_DISPLAY_RULE_IDS)[number];

export type PasswordRuleStatus = Record<PasswordRuleId, boolean>;

export function checkPasswordRules(password: string): PasswordRuleStatus {
  return {
    minLength: password.length >= MIN_PASSWORD_LENGTH,
    special: /[^A-Za-z0-9]/.test(password),
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
}

/**
 * Password policy for newly set passwords:
 * at least 8 characters, with upper, lower, digit, and a special character.
 */
export function isStrongPassword(password: string): boolean {
  const rules = checkPasswordRules(password);
  return (Object.keys(rules) as PasswordRuleId[]).every((id) => rules[id]);
}

export function displayPasswordRulesMet(password: string): boolean {
  const rules = checkPasswordRules(password);
  return PASSWORD_DISPLAY_RULE_IDS.every((id) => rules[id]);
}
